<template>
  <div class="p-5 flex flex-col">
    <p class="font-semibold text-3xl">
      {{ this.$route.meta.title }}
    </p>
    <div class="top-container">
      <vs-button
        type="filled"
        color="primary"
        class="px-2 self-center font-semibold"
        @click="onAddParentGroup"
      >
        Add Parent Group
      </vs-button>
      <div class="relative w-1/4 ml-auto h-fit">
        <vs-input
          icon-no-border
          class="ml-auto border-primary border rounded-full w-full z-0"
          v-model="tempSearch"
          icon="search"
          placeholder="Search here..."
          @keyup.enter="onEnterSearch"
        />
        <div
          v-if="tempSearch.length"
          class="absolute cursor-pointer z-10"
          style="top: 12px; right: 12px;"
          @click="tempSearch = ''"
        >
          <vs-icon
            icon="clear"
            size="small"
          />
        </div>
      </div>
    </div>
    <div class="flex justify-between gap-4 overflow-y-hidden">
      <div
        class="bottom-left-container vs-con-loading__container"
        id="menuConfigurationContainer"
      >
        <h2 class="text-primary font-medium">
          Menu Configuration
        </h2>
        <draggable
          tag="div"
          item-key="id"
          :group="{ name: 'parent', put: true, pull: true }"
          ghost-class="drag-ghost"
          :animation="400"
          class="bg-grey-005 mt-2 rounded-lg p-4 pb-8 flex flex-col gap-1 overflow-y-auto root-draggable h-full"
          v-model="selectedMenus"
          handle=".drag-handle"
          :class="isStartDrag && 'dragArea'"
          :move="handleMove"
          @end="onDragEnd"
          @start="onStart"
        >
          <div
            v-for="menu in selectedMenus"
            :key="menu.id"
            class="flex flex-col gap-1"
          >
            <div>
              <menu-item
                :search="search"
                :menu="menu"
                :on-change-name="(value) => onChangeName(menu, value, 'parent')"
                :on-group-add="() => onAddSubGroup(menu)"
                :on-upload="handleUploadIcon"
                :is-processing="isIdOnProcessing === menu.id"
                :on-delete="menu.is_feature ? null : () => confirmationModalShown = { isShow: true, attr: { menuId: menu.id, type: 'parent', menuName: menu.name } }"
                :on-cancel-edit="() => handleEdit(menu, false, 'parent')"
                :on-redo="menu.is_feature ? () => onDelete({ menuId: menu.id, type: 'parent' }) : null"
                :on-edit="menu.is_feature ? null : () => handleEdit(menu, true, 'parent')"
              />
            </div>
            <draggable
              v-if="!menu.is_feature"
              tag="div"
              item-key="id"
              :group="{ name: 'child', pull: true, put: true }"
              ghost-class="drag-ghost"
              class="flex flex-col gap-1 child-draggable"
              :class="isStartDrag && 'dragArea'"
              :animation="400"
              v-model="menu.children"
              handle=".drag-handle"
              :move="handleMove"
              @end="onDragEnd"
              @start="onStart"
            >
              <div
                v-for="child in menu.children"
                :key="child.id"
                class="flex flex-col gap-1"
              >
                <menu-item
                  :menu="child"
                  custom-style="pl-10"
                  :search="search"
                  :on-change-name="(value) => onChangeName(child, value, 'child', menu.id)"
                  :on-edit="() => handleEdit(child, true , 'child', menu.id)"
                  :on-cancel-edit="() => handleEdit(child, false , 'child', menu.id)"
                  :on-redo="child.is_feature ? () => onDelete({ menuId: child.id, type: 'child', parentId: menu.id }) : null"
                  :on-delete="child.is_feature ? null : () => confirmationModalShown = {
                    isShow: true, attr: { menuId: child.id, type: 'child', menuName: child.name, parentId: menu.id}
                  }"
                />
                <draggable
                  v-if="!child.is_feature"
                  tag="div"
                  item-key="id"
                  :group="{ name: 'grand-child', pull: true, put: true }"
                  ghost-class="drag-ghost"
                  class="flex flex-col gap-1 grand-child-draggable"
                  :animation="400"
                  v-model="child.children"
                  :class="isStartDrag && 'dragArea'"
                  handle=".drag-handle"
                  :move="handleMove"
                  @end="onDragEnd"
                  @start="onStart"
                >
                  <div
                    v-for="grandChild in child.children"
                    :key="grandChild.id"
                  >
                    <menu-item
                      :menu="grandChild"
                      :search="search"
                      custom-style="pl-16"
                      :on-edit="() => handleEdit(grandChild, true, 'grand-child', child.id, menu.id)"
                      :on-cancel-edit="() => handleEdit(grandChild, false, 'grand-child', child.id, menu.id)"
                      :on-change-name="(value) => onChangeName(grandChild, value, 'grand-child', child.id, menu.id)"
                      :on-redo="() => onDelete({ menuId: grandChild.id, type: 'grand-child', parentId: child.id, grandParentId: menu.id })"
                    />
                  </div>
                </draggable>
              </div>
            </draggable>
          </div>
        </draggable>
      </div>
      <div
        class="bottom-right-container vs-con-loading__container"
        id="availableMenuContainer"
      >
        <h2 class="text-primary font-medium">
          Available Features
        </h2>
        <draggable
          tag="div"
          item-key="id"
          :group="{ name: 'available-menu', pull: 'clone', put: false }"
          ghost-class="drag-ghost"
          class="bg-grey-005 mt-2 rounded-lg p-4 flex flex-col gap-1 overflow-y-auto"
          :animation="400"
          v-model="availableMenus"
          handle=".drag-handle"
          @end="isStartDrag = false"
          @start="isStartDrag = true"
        >
          <div
            v-for="aMenu in availableMenus"
            :key="aMenu.id"
          >
            <menu-item
              :search="search"
              :is-available-menu="true"
              :is-feature-already-selected="isFeatureAlreadySelected(aMenu)"
              :menu="aMenu"
            />
          </div>
        </draggable>
      </div>
    </div>
    <div class="flex flex-row mt-4 justify-end gap-4">
      <vs-button
        class="button shadow-primary font-semibold"
        color="primary"
        :disabled="!isMenuConfigurationChanged()"
        @click="onSaveChanges"
      >
        Save Changes
      </vs-button>
      <vs-button
        class="button font-semibold p-2"
        color="danger"
        @click="discardChangesModalShown = true"
        :disabled="!isMenuConfigurationChanged()"
      >
        Discard Changes
      </vs-button>
    </div>
    <pop-up
      :active.sync="confirmationModalShown.isShow"
      is-warning
      title="Delete Menu Group"
      left-button="Delete"
      right-button="Cancel"
      @close="() => confirmationModalShown.isShow = false"
      @leftButton="() => onDelete(confirmationModalShown.attr)"
      @rightButton="() => confirmationModalShown.isShow = false"
    >
      <p class="text-center mt-2">
        Are you sure you want to delete group
      </p>
      <p class="text-center">
        <span class="font-semibold">
          {{ confirmationModalShown.attr.menuName }}
        </span>
        and it's submenu?
      </p>
      <p class="text-center">
        This action cannot be undone
      </p>
    </pop-up>
    <pop-up
      :active.sync="discardChangesModalShown"
      is-warning
      title="Delete All Changes"
      left-button="Discard"
      right-button="Cancel"
      @close="() => discardChangesModalShown = false"
      @leftButton="() => onDiscardChanges()"
      @rightButton="() => discardChangesModalShown = false"
    >
      <p class="text-center mt-2">
        Are you sure you want to <span class="font-bold">discard all changes</span>
      </p>
      <p class="text-center">
        This action cannot be undone
      </p>
    </pop-up>
  </div>
</template>

<script>
import _, { cloneDeep } from 'lodash'
import draggable from 'vuedraggable'
import { mapGetters } from 'vuex'

import PopUp from '@/components/popUp/index.vue'
import { MAX_FILE_UPLOAD_SIZE_BYTES, PAGE_TITLE } from '@/fixtures/Constants'
import MenuItem from '@/views/settings/menuManagement/menuItem'

export default {
  metaInfo() {
    return {
      title: `${PAGE_TITLE} ${this.$route.meta.title}`
    }
  },
  components: { PopUp, MenuItem,  draggable },
  computed: {
    ...mapGetters({
      dataSelectedMenus: 'MenuManagement/selectedMenus',
      dataAvailableMenus: 'MenuManagement/availableMenus'
    })
  },
  async mounted() {
    await Promise.all([
      this.fetchSelectedMenus(),
      this.fetchAvailableMenus()
    ])
  },
  watch: {
    dataSelectedMenus: {
      handler(val) {
        if(!val.length) return;
        this.selectedMenus = []
        this.selectedMenus = val.map(item => ({
          ...item,
          type: 'parent',
          isEditing: false,
          children: item.children ? item.children.map(child => ({
            ...child,
            type: 'child',
            isEditing: false,
            isSubGroup: !child.is_feature,
            children: child.children ?  child.children.map(grandChild => ({
              ...grandChild,
              type: 'grand-child',
              isEditing: false
            })) : []}
          )): []}
        ))
        this.originalMenus = cloneDeep(this.selectedMenus)
      },
      deep: true
    },
    dataAvailableMenus: {
      handler(val) {
        if(!val) return
        this.availableMenus = val.map(item => ({...item, children: []}))
        this.originalAvailableMenus = cloneDeep(this.availableMenus)
      }
    },
    isMenuConfigurationBusy: {
      immediate: true,
      handler(val) {
        if (val) {
          this.$nextTick(() => {
            const containerEl = this.$el && this.$el.querySelector('#menuConfigurationContainer')
            if (!containerEl) return

            this.$vs.loading({
              type: 'radius',
              color: 'secondary',
              container: containerEl,
              scale: 0.5
            })
          })

          return
        }

        this.$nextTick(() => {
          this.$vs.loading.close('#menuConfigurationContainer > .con-vs-loading')
        })
      }
    },
    isAvailableMenuBusy: {
      immediate: true,
      handler(val) {
        if (val) {
          this.$nextTick(() => {
            const containerEl = this.$el && this.$el.querySelector('#availableMenuContainer')
            if (!containerEl) return

            this.$vs.loading({
              type: 'radius',
              color: 'secondary',
              container: containerEl,
              scale: 0.5
            })
          })
          return
        }

        this.$nextTick(() => {
          this.$vs.loading.close('#availableMenuContainer > .con-vs-loading')
        })
      }
    },
    search: {
      handler(val) {
        const allName = this.extractNames(this.selectedMenus)
        const foundedMenu = allName.find(name => name.toLowerCase().includes(val.toLowerCase()))
        if(!foundedMenu) this.showFailedNotification(this, `Menu with name "${val}" not found`)
      },
      deep: true
    }
  },
  data() {
    return {
      confirmationModalShown: {
        isShow: false,
        attr: {
          type: '',
          menuId: 0,
          parentId: 0,
          grandParentId: 0,
          menuName: ''
        }
      },
      discardChangesModalShown: false,
      originalMenus: [],
      selectedMenus: [],
      availableMenus: [],
      originalAvailableMenus: [],
      tempSearch: '',
      search: '',
      isMenuConfigurationBusy: false,
      isAvailableMenuBusy: false,
      isIdOnProcessing: '',
      isStartDrag: false
    }
  },
  methods: {
    onEnterSearch() {
      this.search = this.tempSearch
    },
    deepEqual(a, b) {
      if (a === b) return true;

      const keysA = Object.keys(a);
      const keysB = Object.keys(b);

      if (keysA.length !== keysB.length) return false;

      for (const key of keysA) {
        if (!this.deepEqual(a[key], b[key])) {
          return false;
        }
      }
      return true;
    },
    isMenuConfigurationChanged() {
      if(this.startEditingValidation(this.selectedMenus)) return false
      if (this.originalMenus.length !== this.selectedMenus.length) return true;

      for (let i = 0; i < this.originalMenus.length; i++) {
        if (!this.deepEqual(this.originalMenus[i], this.selectedMenus[i])) {
          return true;
        }
      }

      return false;
    },
    onChangeName(menu, value, type, parentId, grandParentId ) {
      const names = this.extractNames(this.selectedMenus).filter(name => name !== menu.name)
      if(value.length < 3) return this.showFailedNotification(this, 'Menu name must be at least 3 characters long')
      if(names.includes(value)) return this.showFailedNotification(this, 'Menu name already exists')
      if(typeof menu.id === 'string') {
        menu.id = parseInt(menu.id.split('-')[2])
      }
      this.$set(menu, 'name', value)
      this.handleEdit(menu, false, type, parentId, grandParentId)
    },
    async onDiscardChanges() {
      await Promise.all([
        this.fetchSelectedMenus(),
        this.fetchAvailableMenus()
      ])
      this.discardChangesModalShown = false
    },
    populatePayload(obj, index, parentId = null) {
      return {
        id: obj?.isNewMenu ? `${obj.id}` : obj.id,
        name: obj.name,
        order_level: index + 1,
        icon_id: parentId ? null : (obj?.icon?.fileId || obj?.icon_id),
        parent_id: parentId,
        ...(obj.children && obj.children.length && { children: obj.children.map((child, childIndex) => this.populatePayload(child, childIndex, obj.id)) })
      }
    },
    async onSaveChanges() {
      const payload = { menu_configuration: this.selectedMenus.map((parent, parentIndex) => this.populatePayload(parent, parentIndex)) }
      try {
        this.isMenuConfigurationBusy = true;
        await this.$store.dispatch('MenuManagement/updateMenuConfiguration', payload)
        this.showSuccessNotification(this, 'Menu Configuration successfully updated.')
        await this.fetchSelectedMenus()
        await this.fetchAvailableMenus()
        await this.updatePermissions()
      } catch ({ data }) {
        const message = data?.error?.message || 'Failed to save menu configuration, please try again'
        this.showFailedNotification(this, message)
      } finally {
        this.isMenuConfigurationBusy = false;
      }

    },
    findParentByChildren(idChildren, items) {
      for (const item of items) {
        if (item.children.find(child => child.id === idChildren)) {
          return item;
        }
        if (item.children && item.children.length) {
          const found = this.findParentByChildren(idChildren, item.children);
          if (found) return found;
        }
      }
      return null;
    },
    async handleUploadIcon(event, menuId) {
      this.isIdOnProcessing = menuId;
      const file = event.target.files[0]
      if (file) {
        if (file.size > MAX_FILE_UPLOAD_SIZE_BYTES) {
          this.showFailedNotification(this, 'File may not be greater than 5MB')
          event.target.value = '' // Reset value to trigger onchange listener
          return
        }
        try {
          const data = await this.$store.dispatch('MenuManagement/uploadMenuIcon', { file })
          this.selectedMenus.forEach(item => {
            if(item.id === menuId) {
              item.icon = data
            }
          })
          this.selectedMenus = [...this.selectedMenus]
        } catch {
          this.showFailedNotification(this, 'Failed to upload icon, please try again')
        } finally {
          this.isIdOnProcessing = '';
        }
      }
    },
    findFeatureById(list, id) {
      for (const item of list) {
        if (item.id === id && item.is_feature === true) {
          return true;
        }
        if (item.children && item.children.length) {
          const found = this.findFeatureById(item.children, id);
          if (found) return true;
        }
      }
      return null;
    },
    isFeatureAlreadySelected(menu) {
      return this.findFeatureById(this.selectedMenus, menu.id)
    },
    async fetchSelectedMenus() {
      this.isMenuConfigurationBusy = true;
      try {
        await this.$store.dispatch('MenuManagement/getSelectedMenus')
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch selected menus, please try again')
      } finally {
        this.isMenuConfigurationBusy = false;
      }
    },
    async fetchAvailableMenus() {
      this.isAvailableMenuBusy = true;
      try {
        await this.$store.dispatch('MenuManagement/getAvailableMenus')
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch available menus, please try again')
      } finally {
        this.isAvailableMenuBusy = false;
      }
    },
    async updatePermissions() {
      try {
        await this.$store.dispatch('Auth/updatePermissions')
      } catch (e) {
        this.showFailedNotification(this, 'Failed to update permissions, please try again')
      }
    },
    startEditingValidation(menus = []) {
      return menus.find(menu => {
        if (menu.isEditing) return true
        if (menu.children && menu.children.length) {
          return this.startEditingValidation(menu.children)
        } else {
          return false
        }
      })
    },
    onAddParentGroup() {
      if(this.startEditingValidation(this.selectedMenus)) {
        this.showFailedNotification(this, 'Please finish editing the current row before editing another one.')
        return
      }
      const lastIds = _.max(this.extractIds(this.selectedMenus))
      this.selectedMenus.unshift({
        id: `new-parent-${ lastIds + 1 }`,
        name: '',
        type: 'parent',
        is_feature: false,
        isEditing: true,
        isNewMenu: true,
        children: []
      })
    },
    onAddSubGroup(group) {
      if(this.startEditingValidation(this.selectedMenus)) {
        this.showFailedNotification(this, 'Please finish editing the current row before editing another one.')
        return
      }
      const lastIds = _.max(this.extractIds(this.selectedMenus))
      group.children.unshift({
        id: `new-sub-${ lastIds + 1 }`,
        name: '',
        type: 'child',
        is_feature: false,
        isEditing: true,
        isSubGroup: true,
        isNewMenu: true,
        children: []
      })
    },
    extractNames(arr, result = []) {
      arr.forEach(item => {
        result.push(item.name);
        if (item.children) this.extractNames(item.children, result);
      });
      return result;
    },
    extractIds(arr, result = []) {
      arr.forEach(item => {
        result.push(item.id);
        if (item.children) this.extractIds(item.children, result);
      });
      return result;
    },
    onDelete({ menuId, type, parentId, grandParentId }) {
      if (type === 'parent') {
        this.selectedMenus = this.selectedMenus.filter(item => item.id !== menuId)
      } else {
        const rootId = type === 'child' ? parentId : grandParentId
        const root = this.selectedMenus.find(item => item.id === rootId)
        if (root) {
          if (type === 'child') {
            this.$set(root, 'children', root.children.filter(child => child.id !== menuId))
          } else {
            const parent = root.children.find(child => child.id === parentId)
            if (parent) {
              this.$set(parent ,'children', parent.children.filter(grandChild => grandChild.id !== menuId))
            }
          }
        }
      }
      this.availableMenus = cloneDeep(this.originalAvailableMenus)
      this.confirmationModalShown = {
        isShow: false,
        attr: {
          type: '',
          menuId: 0,
          parentId: 0,
          grandParentId: 0
        }
      };
    },
    handleEdit(menu, value, type, parentId, grandParentId) {
      if(value && this.startEditingValidation(this.selectedMenus)) {
        this.showFailedNotification(this, 'Please finish editing the current row before editing another one.')
        return
      }
      if (!value && typeof menu.id === 'string' && menu.id.includes('new')) {
        this.onDelete({ menuId: menu.id, type, parentId, grandParentId })
        return
      }

      let targetItem = null
      if (type === 'parent') {
        targetItem = this.selectedMenus.find(item => item.id === menu.id)
      } else if (type === 'child') {
        const root = this.selectedMenus.find(item => item.id === parentId)
        targetItem = root ? root.children.find(child => child.id === menu.id) : null
      } else {
        const root = this.selectedMenus.find(item => item.id === grandParentId)
        const parent = root ? root.children.find(child => child.id === parentId) : null
        targetItem = parent ? parent.children.find(grandChild => grandChild.id === menu.id) : null
      }

      if (targetItem) {
        if(typeof targetItem.id === 'string') {
          targetItem.id = parseInt(targetItem.id.split('-')[2])
        }
        this.$set(targetItem, 'isEditing', value)
      }
    },
    onStart() {
      this.isStartDrag = true
    },
    onDragEnd() {
      this.isStartDrag = false
    },
    handleMove(evt) {
      const { draggedContext, relatedContext } = evt
      const draggedParent = this.findParentByChildren(draggedContext.element.id, this.selectedMenus)
      const droppedParent = this.findParentByChildren(relatedContext?.element?.id, this.selectedMenus)
      const isSubGroup = draggedContext.element.hasOwnProperty('isSubGroup') ? draggedContext.element.isSubGroup : null
      const isRestricted = draggedContext.element.hasOwnProperty('is_restricted') ? draggedContext.element.is_restricted : null
      if(draggedContext?.element?.type === 'parent' && relatedContext?.element?.type !== 'parent') return false
      if(draggedParent !== droppedParent && (isSubGroup || isRestricted)) return false
    }
  }
}
</script>

<style scoped>
.top-container {
  @apply p-5 mt-5 flex flex-row bg-white rounded-lg;
}

.bottom-left-container {
  @apply p-5 mt-5 flex flex-col  bg-white rounded-lg;
  width: 65%;
}

.bottom-right-container {
  @apply p-5 mt-5 flex flex-col bg-white rounded-lg;
  width: 35%;
}

/deep/.vs-input--placeholder {
  padding-left: calc(1rem + 24px) !important;
}

/deep/.vs-input--input {
  @apply rounded-full !important;
}

/deep/ .vs-con-input {
  outline: none !important;
}

/deep/.vs-input--input {
  @apply bg-white;
  @apply h-12 rounded-lg;
  padding-left: calc(1rem + 24px) !important;
  border: 0 solid transparent !important;
}

/deep/.vs-input--icon {
  top: auto;
  left: 0.5rem;
  @apply text-primary;
}

.drag-ghost {
  opacity: 0.4;
}

.dragArea {
  padding-bottom: 16px;
  min-height: 50px;
  border: 2px dashed #ccc;
}
</style>